import React, { useMemo, useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import LoadingBox from '@pzh/crm/src/Modules/loading/components/LoadingBox';
import { useQuestionnairesByCustomerContactId } from '@pzh/crm/src/Modules/questionnaires/questionnairesSlice';
import { useContactsByCustomerId } from 'Modules/contacts/contactsSlice';
import { useApi } from 'Modules/api/components/ApiProvider';
import Summary from '@pzh/crm/src/Modules/questionnaires/components/pdfDocumentView/pages/Summary';
import { pdf } from '@react-pdf/renderer';
import { useCustomer } from '../../../../../customers/customersSlice';
export const CustomerLatestQuestionairePdfAttachment = ({ customerId, name, value, onChange }) => {
    const { cloud } = useApi();

    const handleChange = useMemo(
        () => (value) => {
            const newValue = value;
            onChange(name, newValue);
        },
        [onChange, name]
    );

    const [exportComplete, setExportComplete] = useState(false);
    const [exportError, setExportError] = useState(false);
    const [contacts] = useContactsByCustomerId(customerId);

    const patients = contacts.filter((c) => c.isPatient);
    const primaryContact = contacts.filter((c) => c.isPrimaryContact)[0];
    const [customer] = useCustomer(customerId);

    const [
        questionnaires,
        { loading: questionnaireLoading, initialized: questionnaireInitialized, error: questionnaireError },
    ] = useQuestionnairesByCustomerContactId(customerId);

    const error = questionnaireError;
    const loading = questionnaireLoading;
    const initialized = questionnaireInitialized;

    const hasExportPdfsRun = useRef(false); // Track if exportPdfs has already run

    useEffect(() => {
         const generatePdfForPatient = async (patient, questionnaire) => {
            const data = {
                maincontact: primaryContact,
                patient,
                questionnaire,
            };

            const blob = await pdf(
                <Summary
                    data={data}
                    customer={customer}
                    questionnaireCreatedDate={questionnaire?.createdAt}
                />
            ).toBlob();
            return blob;
        };

        const exportPdfs = async () => {
            if (contacts && patients.length > 0 && primaryContact && questionnaires.length > 0 && !exportComplete) {

                if (hasExportPdfsRun.current)
                    return; // Prevent re-running if already executed
                hasExportPdfsRun.current = true; // Mark as executed

                try {
                    const uploadPaths = [];
                    const pdfBlobs = []; // Array to store generated PDF blobs

                    // Step 1: Generate all PDFs
                    console.log('Generating PDFs...');
                    for (const patient of patients) {
                        const patient_questionnaires = questionnaires
                            .filter((q) => q.contactId === patient.id)
                            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                        if (!patient_questionnaires) continue;

                        const questionnairesSorted = patient_questionnaires.sort((a, b) => a.id - b.id)
                        const [questionnaire] = questionnairesSorted.slice(-1);
                        if (!questionnaire) continue;

                        const pdfBlob = await generatePdfForPatient(patient, questionnaire);
                        pdfBlobs.push({ pdfBlob, patientId: patient.id }); // Store blob with patient ID
                    }

                    // Step 2: Upload all PDFs
                    console.log('Uploading PDFs to cloud storage...');
                    for (const { pdfBlob, patientId } of pdfBlobs) {
                        const upload_data = {
                            path: `/PzH/Kunden/${customerId}/`,
                            file: `questionnaire_summary_${customerId}_${patientId}.pdf`,
                        };

                        const upload_full_path = `${upload_data.path}${upload_data.file}`;
                        uploadPaths.push(upload_full_path);

                        const temporaryFile = new File([pdfBlob], `tempfile_${customerId}_${patientId}.pdf`, { type: 'application/pdf' });
                        await cloud.upload(upload_full_path, temporaryFile);
                    }

                    // Mark as complete only after all uploads are done
                    setExportComplete(true);
                    handleChange(uploadPaths);
                } catch (upload_error) {
                    console.error('ERROR: Uploading PDF files:', upload_error);
                    setExportError(true);
                }
            }
        };

        exportPdfs();

    }, [contacts, patients, primaryContact, questionnaires, exportComplete, cloud, customerId, customer, handleChange]);

    if (error) {
        setExportError(true);
    }

    return (
        <LoadingBox loading={loading} initialized={initialized}>
            <p>Erhebungsbogen (PDF)
                {!exportError && !exportComplete && ' - Wird geladen...'}
                {!exportError && exportComplete && ' - Als Anhang bereit'}
                {exportError && ' - [Fehler beim Laden des Erhebungsbogen!]'}
            </p>
        </LoadingBox>
    );
};

CustomerLatestQuestionairePdfAttachment.propTypes = {
    customerId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

CustomerLatestQuestionairePdfAttachment.defaultProps = {};

export default CustomerLatestQuestionairePdfAttachment;
